import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PackageSearch, Search } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/ErrorState';
import { clearSearch } from '../../features/tracking/trackingSlice';
import { searchOrders } from '../../features/tracking/trackingThunks';
import { TrackingField } from './components/TrackingField';
import { TrackingLayout } from './components/TrackingLayout';
import { TrackingResults } from './components/TrackingResults';

const MODES = {
  REFERENCE: 'reference',
  ORDER_ID: 'orderId',
};

/**
 * Where a customer looks up their own parcel.
 *
 * Two ways in, because there are two numbers a person might have: the one the
 * store gave them when they bought — which they know, and which is paired with
 * their phone so it is not enough on its own — and the Ninja Hubs one, which
 * they only have if it arrived in a link.
 *
 * Ordered as the old page ordered them, and opening on the first. The old page
 * opened on neither and rendered no form until you picked one, which read as a
 * broken screen.
 */
export const Tracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { results, hasSearched, isSearching, searchError } = useSelector(
    (state) => state.tracking
  );

  const [mode, setMode] = useState(MODES.ORDER_ID);
  const [idReference, setIdReference] = useState('');
  const [phone, setPhone] = useState('');
  const [orderId, setOrderId] = useState('');
  const [validationError, setValidationError] = useState('');

  // Nothing of a previous visit survives the mount: this page is often opened
  // on a shared phone.
  useEffect(() => {
    dispatch(clearSearch());
  }, [dispatch]);

  const switchMode = (next) => {
    setMode(next);
    setValidationError('');
    dispatch(clearSearch());
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setValidationError('');

    if (mode === MODES.ORDER_ID) {
      if (!orderId.trim()) {
        setValidationError('Escribe el número de pedido.');
        return;
      }
      dispatch(searchOrders({ orderId: orderId.trim() }));
      return;
    }

    // The backend takes a phone on its own, but a reference without one would
    // match other people's orders, so it refuses that pair.
    if (!phone.trim()) {
      setValidationError('Necesitamos el teléfono con el que hiciste la compra.');
      return;
    }

    dispatch(
      searchOrders({
        idReference: idReference.trim() || undefined,
        phone: phone.trim(),
      })
    );
  };

  const error = validationError || searchError;

  return (
    <TrackingLayout
      testId="tracking-page"
      title="Sigue tu pedido"
      subtitle="Busca con los datos que usaste al comprar y te mostramos dónde va."
    >
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border p-6 sm:p-8">
        <div role="tablist" aria-label="Forma de búsqueda" className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            role="tab"
            aria-selected={mode === MODES.ORDER_ID}
            data-testid="tracking-mode-order-id"
            onClick={() => switchMode(MODES.ORDER_ID)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
              mode === MODES.ORDER_ID
                ? 'bg-ninja-green border-ninja-green text-white'
                : 'border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white'
            }`}
          >
            Con el número de pedido
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === MODES.REFERENCE}
            data-testid="tracking-mode-reference"
            onClick={() => switchMode(MODES.REFERENCE)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
              mode === MODES.REFERENCE
                ? 'bg-ninja-green border-ninja-green text-white'
                : 'border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white'
            }`}
          >
            Con mi orden de compra
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="tracking-search-form" className="space-y-5">
          {mode === MODES.REFERENCE ? (
            <>
              <TrackingField
                id="tracking-reference"
                testId="tracking-reference-input"
                label="Orden de compra"
                placeholder="Ej: 123456"
                hint="El número que te dio la tienda al comprar."
                value={idReference}
                onChange={setIdReference}
              />
              <TrackingField
                id="tracking-phone"
                testId="tracking-phone-input"
                label="Teléfono"
                type="tel"
                placeholder="Ej: +56 9 1234 5678"
                hint="El mismo que dejaste al momento de la compra."
                value={phone}
                onChange={setPhone}
              />
            </>
          ) : (
            <TrackingField
              id="tracking-order-id"
              testId="tracking-order-id-input"
              label="Número de pedido"
              placeholder="Ej: 78910"
              hint="El número de Ninja Hubs, el que viene en el link de seguimiento."
              value={orderId}
              onChange={setOrderId}
            />
          )}

          <Button
            type="submit"
            variant="primary"
            icon={Search}
            loading={isSearching}
            loadingLabel="Buscando..."
            fullWidth
          >
            Buscar
          </Button>
        </form>

        {error && <ErrorState variant="inline" title={error} className="mt-5" />}
      </div>

      {results.length > 0 && (
        <TrackingResults results={results} onSelect={(id) => navigate(`/track-order/${id}`)} />
      )}

      {hasSearched && !isSearching && !error && results.length === 0 && (
        <div
          data-testid="tracking-empty"
          className="mt-8 py-10 flex flex-col items-center justify-center text-center"
        >
          <div className="w-16 h-16 bg-ninja-gray dark:bg-[#2d2d2d] rounded-full flex items-center justify-center mb-4">
            <PackageSearch size={24} className="text-text-muted" />
          </div>
          <h2 className="text-base font-bold text-ninja-dark dark:text-white mb-2">
            No encontramos pedidos
          </h2>
          <p className="text-text-secondary max-w-sm">Revisa los datos e inténtalo otra vez.</p>
        </div>
      )}
    </TrackingLayout>
  );
};
